import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import useUserManagementPermissions from '../hooks/useUserManagementPermissions';
import useUserSx from '../hooks/useUserSx';
import { isSameUser } from '../utils/userAccessUtils';

export default function UserRowActions({
  user,
  rowIndex,
  onEdit,
  onChangePassword,
  onUnlock,
  onDeactivate,
  onActivate,
  onDelete,
}) {
  const styles = useUserSx();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const {
    canEdit,
    canDelete: canDeleteUsers,
    canChangePassword,
    canUnlock,
    canSetStatus,
  } = useUserManagementPermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const canDelete = canDeleteUsers && !isSameUser(loggedInUser, user);
  const isActive = user?.is_active !== false;
  const canToggleStatus = canSetStatus && !isSameUser(loggedInUser, user);

  const menuItems = [];

  if (canEdit) {
    menuItems.push({
      key: 'edit',
      label: 'Edit',
      onClick: () => onEdit?.(user),
    });
  }
  if (canChangePassword) {
    menuItems.push({
      key: 'password',
      label: 'Change Password',
      onClick: () => onChangePassword?.(user),
    });
  }
  if (canUnlock) {
    menuItems.push({
      key: 'unlock',
      label: 'Unlock User',
      onClick: () => onUnlock?.(user),
    });
  }
  if (canToggleStatus && isActive) {
    menuItems.push({
      key: 'deactivate',
      label: 'Deactivate',
      destructive: true,
      onClick: () => onDeactivate?.(user),
    });
  }
  if (canToggleStatus && !isActive) {
    menuItems.push({
      key: 'activate',
      label: 'Activate',
      onClick: () => onActivate?.(user),
    });
  }
  if (canDelete) {
    menuItems.push({
      key: 'delete',
      label: 'Delete',
      destructive: true,
      onClick: () => onDelete?.(user, rowIndex),
    });
  }

  if (!menuItems.length) {
    return null;
  }

  const handleClose = () => setAnchorEl(null);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box sx={styles.actionColumnCell}>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label="Actions"
        sx={styles.rowActionButton}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            sx={item.destructive ? { color: 'error.main' } : undefined}
            onClick={() => {
              handleClose();
              item.onClick();
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

UserRowActions.propTypes = {
  user: PropTypes.object,
  rowIndex: PropTypes.number,
  onEdit: PropTypes.func,
  onChangePassword: PropTypes.func,
  onUnlock: PropTypes.func,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
};
